// Layouts
import { SidebarLayout } from 'layouts';


// Library Methods
import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { ErrorBoundary } from 'react-error-boundary';
import 'react-toastify/dist/ReactToastify.css';
import './styles/base.scss';

// Containers
// eslint-disable-next-line import/order
import {
    Login,
    ForgotPassword,
    SetPassword,
    MerchantOnboard,
    UserManagement,
    RoleManagement,
    UserVerification,
    Transactions,
    Refund,
    CommProfile,
    ManageBin,
    RoleVerification,
    Profile,
    PaymentLink,
    AuditTrail,
    Dashboard,
    ErrorPage,
    CheckoutCustom,
    Settlement,
    MerchantAdjustments,
    BulkRefund,
    MerchantOnboardStatus,
    Invoice,
    BulkPaymentLink,
    Maker,
    Checker,
    Mandate,
    Chargeback,
    MerchantLegalEntity,
    PaymentGatewayCredentials,
    BulkAccountVerfication,
    TidOnboard,
    PaymentPage
} from './containers';

// Components
// eslint-disable-next-line import/order
import { AuthRoute, Loader } from './components';

// Actions
// eslint-disable-next-line import/order
import {
    createSessionRequest,
    isLoggedInRequest,
    userProfileRequest,
    getUserAccessibleItemsRequest,
} from './redux/common/auth/auth.actions';

// Styles
import './styles/style.scss';
// Starting point of our react app
function App(props) {
    const { actions, auth, loader } = props;
    const history = useHistory();

    useEffect(() => {
        // Create a new session on app load
        actions.createSessionRequest(history);
    }, [actions, history]);

    useEffect(() => {
        // Check if user is logged in
        if (auth.csrfIdentifier) {
            actions.isLoggedInRequest();
        }
    }, [actions, auth.csrfIdentifier]);

    useEffect(() => {
        // Get user profile
        if (auth.isLoggedIn) {
            actions.userProfileRequest();
            actions.getUserAccessibleItemsRequest();
        }
    }, [actions, auth.isLoggedIn]);

    return (
        <Fragment>
            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={true}
                closeOnClick
                closeButton={false}
                toastClassName="toaster"
            />
            {loader.showLoader && <Loader />}
            {/* Error handling in case of any Javascript Error */}
            <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                    <ErrorPage
                        error={
                            'Oops! Something is wrong. Please try again after some time'
                        }
                        resetErrorBoundary={resetErrorBoundary}
                    />
                )}
            >
                <Switch>
                    {/* Error handling in case of session api fail */}
                    <Route path="/error">
                        <ErrorPage />
                    </Route>
                    {auth.isLoggedInExecuted && (
                        <>
                            <Route exact path="/">
                                <Redirect to="/login" />
                            </Route>
                            <Route path="/login">
                                <Login />
                            </Route>
                            <Route path="/forgotpassword">
                                <ForgotPassword />
                            </Route>
                            <Route path="/resetpassword/:linkToken">
                                <SetPassword />
                            </Route>
                            <SidebarLayout>
                                <AuthRoute
                                    path="/usermanagement"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <UserManagement />
                                </AuthRoute>
                                <AuthRoute
                                    path="/rolemanagement"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <RoleManagement />
                                </AuthRoute>
                                <AuthRoute
                                    path="/userverification"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <UserVerification />
                                </AuthRoute>
                                <AuthRoute
                                    path="/mob"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <MerchantOnboard />
                                </AuthRoute>
                                <AuthRoute
                                    path="/tid-onboarding"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <TidOnboard />
                                </AuthRoute>
                                <AuthRoute
                                    path="/mle-onboarding"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <MerchantLegalEntity />
                                </AuthRoute>
                                <AuthRoute
                                    path="/mobStatus"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <MerchantOnboardStatus />
                                </AuthRoute>
                                <AuthRoute
                                    path="/transactions"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Transactions />
                                </AuthRoute>
                                <AuthRoute
                                    path="/merchant-adjustments"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <MerchantAdjustments />
                                </AuthRoute>
                                <AuthRoute
                                    path="/refund"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Refund />
                                </AuthRoute>
                                <AuthRoute
                                    path="/bulk-refund"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <BulkRefund />
                                </AuthRoute>
                                <AuthRoute
                                    path="/settlement"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Settlement />
                                </AuthRoute>
                                <AuthRoute
                                    path="/invoice"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Invoice />
                                </AuthRoute>
                                <AuthRoute
                                    path="/communication"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <CommProfile />
                                </AuthRoute>
                                <AuthRoute
                                    path="/manage-bin"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <ManageBin />
                                </AuthRoute>
                                <AuthRoute
                                    path="/roleverification"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <RoleVerification />
                                </AuthRoute>
                                <AuthRoute
                                    path="/profile"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Profile />
                                </AuthRoute>
                                <AuthRoute
                                    path="/checkout-customization"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <CheckoutCustom />
                                </AuthRoute>
                                <AuthRoute
                                    path="/dashboard"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Dashboard />
                                </AuthRoute>
                                <AuthRoute
                                    path="/payment-link"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <PaymentLink />
                                </AuthRoute>
                                <AuthRoute
                                    path="/bulk-payment-link"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <BulkPaymentLink />
                                </AuthRoute>
                                <AuthRoute
                                    path="/bulk-account-verification"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <BulkAccountVerfication />
                                </AuthRoute>
                                <AuthRoute
                                    path="/payment-page"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <PaymentPage />
                                </AuthRoute>
                                <AuthRoute
                                    path="/audit-report"
                                    exact
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <AuditTrail />
                                </AuthRoute>
                                <AuthRoute
                                    path="/maker"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Maker />
                                </AuthRoute>
                                <AuthRoute
                                    path="/checker"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Checker />
                                </AuthRoute>
                                <AuthRoute
                                    path="/mandate"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Mandate />
                                </AuthRoute>
                                <AuthRoute
                                    path="/chargeback"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <Chargeback />
                                </AuthRoute>

                                <AuthRoute
                                    path="/payment-gateway-credentials"
                                    isLoggedIn={auth.isLoggedIn}
                                >
                                    <PaymentGatewayCredentials />
                                </AuthRoute>
                            </SidebarLayout>
                        </>
                    )}
                </Switch>
            </ErrorBoundary>
        </Fragment>
    );
}

App.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    loader: PropTypes.object,
};

const mapStateToProps = ({ loader, auth }) => ({
    auth,
    loader,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            createSessionRequest,
            isLoggedInRequest,
            userProfileRequest,
            getUserAccessibleItemsRequest,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
